import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { PrismicRichText } from '@prismicio/react';
import { PrismicNextLink, PrismicNextImage } from '@prismicio/next';
import { PlainRightArrow } from '@/assets/PlainRightArrow';
import styles from './HighlightedJobListings.module.css';

/**
 * Props for `HighlightedJobListings`.
 */
export type HighlightedJobListingsProps =
  SliceComponentProps<Content.HighlightedJobListingsSlice>;

/**
 * Component for "HighlightedJobListings" Slices.
 */
const HighlightedJobListings = ({
  slice,
}: HighlightedJobListingsProps): JSX.Element => {
  const {
    primary: { heading, primary_cta: cta, cta_label: ctaLabel, jobs },
  } = slice;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      style={{
        backgroundColor: '#F3F3F8',
      }}
    >
      <div className="container mx-auto p-4 md:px-6 text-black">
        <div className="flex flex-col-reverse md:flex-row md:items-center md:gap-x-8 lg:gap-x-12 xl:gap-x-16">
          <div className="w-full md:w-1/3 lg:w-2/5 flex flex-col items-start text-left gap-y-6 pb-8 md:mb-0 pt-8 md:pt-0">
            <div
              style={{
                fontWeight: '700',
                fontSize: '40px',
                lineHeight: '120%',
              }}
            >
              <PrismicRichText field={heading} />
            </div>
            <PrismicNextLink
              field={cta}
              className="button-squared"
              style={{
                backgroundColor: '#03BF9E',
              }}
            >
              <div className="flex flex-row gap-x-7">
                <span
                  style={{
                    fontWeight: '700',
                    fontSize: '16px',
                    lineHeight: '150%',
                  }}
                >
                  {ctaLabel}
                </span>
                <PlainRightArrow fill="white" arrowFill="white" />
              </div>
            </PrismicNextLink>
          </div>

          <div
            className={`w-full md:w-2/3 lg:w-3/5
              flex flex-row flex-nowrap items-stretch
              gap-x-6 md:gap-x-8
              overflow-x-auto
              pt-2 pb-4
              ${styles.customScrollbar2}`}
          >
            {jobs.map((item, index) => (
              <div
                className="
                flex flex-col items-start text-left
                gap-y-4
                w-80 sm:w-72 md:w-[17rem] lg:w-[24rem]
                flex-shrink-0
                bg-white
                p-5 md:p-6
                rounded-lg
                shadow-md
                transition-shadow duration-200 hover:shadow-lg"
                key={`open-position-${index}`}
              >
                <div className="w-full h-60 mb-2 overflow-hidden">
                  <PrismicNextImage
                    field={item.card_image}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div
                  style={{
                    fontWeight: '700',
                    fontSize: '16px',
                    lineHeight: '150%',
                  }}
                >
                  <PrismicRichText field={item.title} />
                </div>
                <div
                  style={{
                    fontWeight: '500',
                    fontSize: '14px',
                    lineHeight: '150%',
                  }}
                >
                  <PrismicRichText field={item.summary} />
                </div>
                <div className="flex flex-row items-center justify-between w-full">
                  <div className="flex flex-row items-start">
                    <svg
                      version="1.0"
                      id="Layer_1"
                      xmlns="http://www.w3.org/2000/svg"
                      width="16px"
                      height="16px"
                      viewBox="0 0 64 64"
                      enable-background="new 0 0 64 64"
                      fill="#666666"
                      stroke="#666666"
                    >
                      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        <path
                          fill="#666666"
                          d="M32,0C18.746,0,8,10.746,8,24c0,5.219,1.711,10.008,4.555,13.93c0.051,0.094,0.059,0.199,0.117,0.289l16,24 C29.414,63.332,30.664,64,32,64s2.586-0.668,3.328-1.781l16-24c0.059-0.09,0.066-0.195,0.117-0.289C54.289,34.008,56,29.219,56,24 C56,10.746,45.254,0,32,0z M32,32c-4.418,0-8-3.582-8-8s3.582-8,8-8s8,3.582,8,8S36.418,32,32,32z"
                        ></path>
                      </g>
                    </svg>
                    <span
                      className="pl-1"
                      style={{
                        fontWeight: '500',
                        fontSize: '14px',
                        lineHeight: '150%',
                      }}
                    >
                      {item.location}
                    </span>
                  </div>
                  <div className="flex flex-row items-start">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-4 h-4 mr-1.5 text-gray-500"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 000-1.5h-3.25V5z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span
                      className="pr-8"
                      style={{
                        fontWeight: '500',
                        fontSize: '14px',
                        lineHeight: '150%',
                      }}
                    >
                      {item.job_type}
                    </span>
                  </div>
                </div>
                <PrismicNextLink
                  field={item.card_cta}
                  className="button-squared-black"
                  style={{
                    backgroundColor: '#000000',
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HighlightedJobListings;
